// A16 — Billing report. Same TVP2 §9 styling as CommissionReport:
// bordered Reporting filter card → bordered results card. Customer is a
// debounced-search Autocomplete (preloaded so the dropdown isn't empty
// before the operator types); Month is a MUI <input type="month">. Report
// runs automatically when both are set. Green 'Export and share' CSV
// download button below the table.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { formatHKD } from "@findgeeks/shared";
import { apiFetch } from "@/lib/api";
import { ENV } from "@/lib/env";
import { supabase } from "@/lib/supabase";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { useToast } from "@/components/Toast";

interface CustomerOpt {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}
interface ListAppUsersRes {
  items: CustomerOpt[];
}

interface BillingRow {
  job_id: string;
  event: string;
  date: string;
  billing: number;
  status: string;
}

interface BillingResp {
  items: BillingRow[];
  total: number;
}

export function BillingReport() {
  const { show, ToastEl } = useToast();
  const [customerId, setCustomerId] = useState<string>("");
  const [customerLabel, setCustomerLabel] = useState<CustomerOpt | null>(null);
  const [month, setMonth] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  // Debounced customer search.
  const [input, setInput] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(t);
  }, [input]);

  // Preloaded by default so the dropdown isn't empty before the operator
  // types — same pattern as CommissionReport's engineer picker.
  const search = useQuery<ListAppUsersRes>({
    queryKey: ["app-users", "customer-search", debounced],
    queryFn: () =>
      apiFetch<ListAppUsersRes>(
        `/v1/admin/app-users?role=customer&limit=20&q=${encodeURIComponent(debounced)}`,
      ),
  });

  const report = useQuery<BillingResp>({
    queryKey: ["admin", "reports", "billing", customerId, month],
    queryFn: () =>
      apiFetch<BillingResp>(
        `/v1/admin/reports/billing?customerId=${customerId}&month=${month}`,
      ),
    enabled: Boolean(customerId) && Boolean(month),
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const items = report.data?.items ?? [];
  const start = paginationModel.page * paginationModel.pageSize;
  const slice = items.slice(start, start + paginationModel.pageSize);

  const columns: DataTableColumn<BillingRow>[] = [
    { field: "date", headerName: "Date" },
    {
      field: "billing",
      headerName: "Billing",
      render: (r) => formatHKD(r.billing),
    },
    {
      field: "event",
      headerName: "Event",
      render: (r) => r.event || "-",
    },
  ];

  const exportCsv = async () => {
    if (!customerId || !month) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const res = await fetch(
        `${ENV.API_BASE_URL}/v1/admin/reports/billing.csv?customerId=${customerId}&month=${month}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );
      if (!res.ok) {
        show("CSV export failed", "error");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      show("CSV downloaded", "success");
    } catch (err) {
      show(err instanceof Error ? err.message : "CSV failed", "error");
    }
  };

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 2,
  } as const;

  // Bootstrap-style green Export button (matches the Add buttons elsewhere).
  const exportBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
    backgroundColor: "#5CB85C",
    "&:hover": { backgroundColor: "#4CAE4C", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#A8D5A8",
      color: "rgba(255,255,255,0.85)",
    },
  };

  const labelOf = (o: CustomerOpt) =>
    [o.first_name, o.last_name].filter(Boolean).join(" ") ||
    o.display_name ||
    o.email ||
    o.id;

  return (
    <Box sx={{ p: 3 }}>
      {/* Reporting filter card */}
      <Paper elevation={0} sx={{ ...cardSx, mb: 3, p: 3 }}>
        <Typography sx={sectionHeading}>Reporting</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
          }}
        >
          <Autocomplete<CustomerOpt>
            options={search.data?.items ?? []}
            value={customerLabel}
            filterOptions={(o) => o}
            loading={search.isFetching}
            getOptionLabel={labelOf}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onInputChange={(_, v) => setInput(v)}
            onChange={(_, v) => {
              setCustomerLabel(v);
              setCustomerId(v?.id ?? "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Choose Customer"
                helperText="Type to search customers by name"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
          <TextField
            variant="standard"
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {/* Results card */}
      <Paper elevation={0} sx={{ ...cardSx, overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 3, pb: 2 }}
        >
          <Typography
            sx={{
              fontSize: tokens.font.sizes.body,
              color: tokens.colors.admin.textBody,
            }}
          >
            {customerLabel
              ? `${labelOf(customerLabel)} — ${month}`
              : "Pick a customer to run the report"}
          </Typography>
          {report.data && (
            <Typography sx={{ fontSize: tokens.font.sizes.body }}>
              Total billed: <strong>{formatHKD(report.data.total)}</strong>
            </Typography>
          )}
        </Stack>
        <Box sx={{ px: 3, pb: 2 }}>
          <DataTable
            columns={columns}
            rows={slice}
            rowCount={items.length}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            getRowId={(r) => r.job_id}
            loading={report.isFetching}
            bordered={false}
            emptyMessage={
              !customerId
                ? "No customer selected"
                : "No completed jobs for this customer / period"
            }
          />
        </Box>
        <Box sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            sx={exportBtn}
            disabled={!customerId || !month || items.length === 0}
            onClick={exportCsv}
          >
            Export and share
          </Button>
        </Box>
      </Paper>
      {ToastEl}
    </Box>
  );
}
